/**
 * FAQ content for /faq, the landing mini-FAQ, and the /faq crawl snippet (PBI #1293).
 *
 * Build plugins (`plugins/publicCrawlSnippets.ts`) import this module under Node,
 * so it must stay free of theme, Mantine, or browser imports. That is why it lives
 * outside `landingContent.ts`, which re-exports everything here.
 *
 * Business rules:
 * - No dollar literals. Priced answers are swapped in at runtime from AppSettings by
 *   `resolveFaqWithPricing.ts`; the fallbacks here are what static HTML carries.
 */

import { FaqQuestions } from '../constants/faqQuestions';
import { buildFaqWhoCanOrderAnswer } from '../utils/marketingPricingCopy';
import { AUTO_SEND_DEFINITION, AUTO_SEND_PUBLIC_NAME } from './publicPageMeta';

/** FAQ: Auto Send definition — shared across FAQ page, crawl HTML, and llms.txt. */
export const faqAutoSendAnswer = AUTO_SEND_DEFINITION;

/** FAQ: which plan includes Auto Send (no dollar literals). */
export const faqAutoSendPlanAnswer =
  `${AUTO_SEND_PUBLIC_NAME} is included with the Personal subscription. The free plan lets you schedule static e-cards manually for a limited number of contacts; turn on ${AUTO_SEND_PUBLIC_NAME} when you upgrade to Personal for hands-free delivery on birthdays, anniversaries, and custom dates.`;

/** FAQ: flower pricing (not subscription-gated). Shared static copy — no dollar literals. */
export const faqFlowerPricingAnswer =
  'No — each flower order is charged separately at the florist\u2019s catalog price, with no markup from Scheduled Smiles (a temporary sitewide florist sale may lower the product price when shown). You need a payment method on file; flower ordering is available on the free plan. Member pricing does not change flower cost.';

/**
 * Price-free FAQ fallbacks shown until public subscription config loads (PBI #1193).
 * Priced answers are built via marketingPricingCopy builders once AppSettings amounts arrive.
 */
export const faqFreePlanAnswerFallback =
  'The free plan lets you schedule static e-cards for a limited number of contacts — no credit card required for static e-cards. Animated e-cards are priced per send for free users. Flower and mailed-card ordering are available on the free plan with a payment method on file (pay per order). The Personal subscription unlocks unlimited contacts, contact import from a file, Auto Send, AI writing assistant, and member pricing on mailed cards and animated e-cards.';

export const faqMailedCardPricingAnswerFallback =
  'No — each mailed card is a pay-per-order charge. List and member prices are shown before you confirm. You need a payment method on file; mailed-card ordering is available on the free plan.';

/** FAQ: ordering eligibility — free plan can order; Personal is optional (PBI #1193). */
export const faqWhoCanOrderAnswer = buildFaqWhoCanOrderAnswer();

/**
 * FAQ: file import is a Personal feature (PBI #923). Free accounts add people one at a time.
 * No Google account connection — the user uploads an export.
 */
export const faqContactImportAnswer =
  'Yes, on the Personal plan. From Contacts, choose Import and upload a CSV, vCard, or spreadsheet you exported from Google, Apple, Outlook, Yahoo, or AOL, or use the Scheduled Smiles template. You preview everyone first, set a relationship and tone, then import. You can undo that import within 7 days when those contacts were not edited afterward. The free plan adds people one at a time, up to the free contact limit. Uploaded files are used to create your contacts and are not sent to other companies.';

/**
 * FAQ: member pricing without dollar literals until public config loads.
 */
export const faqMemberPricingAnswerFallback =
  'Member pricing is the lower pay-per-order rate Personal subscribers pay on mailed cards and animated e-cards. Free accounts pay list. Flowers are charged at the florist\u2019s catalog price with no markup from Scheduled Smiles on every plan (temporary sitewide florist sales may apply when shown) — member pricing does not change flower cost. Exact list and member amounts are shown before you confirm.';

const faqFreePlanAnswer = faqFreePlanAnswerFallback;
const faqMailedCardPricingAnswer = faqMailedCardPricingAnswerFallback;
const faqMemberPricingAnswer = faqMemberPricingAnswerFallback;

/**
 * FAQ page — expanded with category groupings.
 * Used on the dedicated /faq page for better scannability and SEO, and as the
 * source of the /faq crawl snippet.
 */
export const faqPage = {
  heroTitle: 'Frequently Asked Questions',
  heroSubtitle:
    'Everything you need to know about scheduling free e-cards, mailed cards, and flower delivery with Scheduled Smiles.',
  contactPrompt: 'Still have a question? We\u2019d love to help.',
  contactEmail: 'info@scheduledsmiles.com',
  categories: [
    {
      title: 'Getting started',
      items: [
        {
          q: FaqQuestions.IsReallyFree,
          a: faqFreePlanAnswer,
        },
        {
          q: 'How far ahead can I schedule?',
          a: 'As far ahead as you like. You can schedule a birthday e-card in advance for next month or next year. Scheduled Smiles also supports recurring e-cards, so a birthday card can be set to send every year automatically. For flower delivery, we recommend scheduling at least 7 days ahead so the local florist has time to prepare. For mailed cards, schedule early enough for printing and postage \u2014 the order flow shows estimated arrival windows.',
        },
        {
          q: 'Do my contacts need to sign up?',
          a: 'No. Recipients do not need an account. E-cards arrive by email, mailed cards arrive in the post, and flowers are delivered to the door \u2014 nothing to install or sign up for.',
        },
        {
          q: FaqQuestions.CanIImportContacts,
          a: faqContactImportAnswer,
        },
      ],
    },
    {
      title: 'Plans and pricing',
      items: [
        {
          q: FaqQuestions.WhoCanOrder,
          a: faqWhoCanOrderAnswer,
        },
        {
          q: FaqQuestions.WhatIsMemberPricing,
          a: faqMemberPricingAnswer,
        },
        {
          q: FaqQuestions.SubscriptionCoversFlowers,
          a: faqFlowerPricingAnswer,
        },
        {
          q: FaqQuestions.SubscriptionCoversMailedCards,
          a: faqMailedCardPricingAnswer,
        },
      ],
    },
    {
      title: 'Personalization',
      items: [
        {
          q: 'Can I add my own photos or videos?',
          a: 'Yes for e-cards. Upload your own images or videos to personalize a free e-card with photos that matter. Professionally designed e-cards with built-in media are also available if you prefer something ready-made. Mailed cards let you personalize the writable message panels; flower orders focus on the arrangement and delivery details.',
        },
        {
          q: 'What if I want to change my message after scheduling?',
          a: 'You can edit or cancel a scheduled e-card at any time before it\u2019s sent. You\u2019ll also get a pre-send reminder a few days before delivery, so you always have a chance to review and tweak your message. Mailed-card and flower orders can be changed while they are still pending, before they are placed with the vendor. Mailed cards for dates close to today may be placed with our mailing partner right away when you submit (or when Cardly\'s estimate requires it), and then they cannot be cancelled in the app.',
        },
      ],
    },
    {
      title: 'Delivery and automation',
      items: [
        {
          q: `What is ${AUTO_SEND_PUBLIC_NAME}?`,
          a: faqAutoSendAnswer,
        },
        {
          q: `Which plan includes ${AUTO_SEND_PUBLIC_NAME}?`,
          a: faqAutoSendPlanAnswer,
        },
        {
          q: 'Will my friend know it was automated?',
          a: 'No. They receive a beautiful, personalized e-card from you via email, a mailed card in the post, or flowers at the door. There\u2019s no mention of scheduling or automation \u2014 it simply looks like you remembered and took the time to send something thoughtful. Because you did.',
        },
        {
          q: 'What happens if I forget to review before it sends?',
          a: 'We send you a notification before delivery so you can review, edit, or let it go as-is. If you don\u2019t respond to the reminder, the gift goes out with what you already approved when you scheduled it.',
        },
      ],
    },
    {
      title: 'Flower delivery',
      items: [
        {
          q: 'How does flower delivery work?',
          a: 'When you schedule a flower delivery, your order is fulfilled by a local florist near the recipient \u2014 supporting local businesses while ensuring fresh, hand-arranged flowers.',
        },
        {
          q: 'How far in advance should I schedule flowers?',
          a: 'Schedule flower delivery in advance \u2014 we recommend at least 7 days before the delivery date so the local florist has time to source and prepare your arrangement. You can schedule further ahead for extra peace of mind.',
        },
        {
          q: 'What if flowers can\u2019t be delivered on the scheduled date?',
          a: 'If your chosen delivery date is unavailable (for example, Sundays or holidays), we\u2019ll automatically try to deliver up to 3 days earlier \u2014 choosing the closest available date before the event. You\u2019ll be notified by email if the date is adjusted. If no dates are available within that window, the order won\u2019t be placed and you won\u2019t be charged.',
        },
        {
          q: 'Can I cancel a flower order?',
          a: 'You can cancel a flower order up until the florist begins fulfilling it. Once the order is placed with the florist, cancellation is no longer possible. You\u2019ll see the cancellation window clearly when placing your order.',
        },
        {
          q: FaqQuestions.SubscriptionCoversFlowers,
          a: faqFlowerPricingAnswer,
        },
      ],
    },
    {
      title: 'Mailed cards',
      items: [
        {
          q: 'How do mailed cards work?',
          a: 'Mail a greeting card online: browse physical card designs, personalize the writable panels, and schedule the arrival date. We print and post the card through our mailing partner so it arrives on time \u2014 you get reminders before it goes out.',
        },
        {
          q: 'How far in advance should I schedule a mailed card?',
          a: 'Schedule early enough for printing and postage. The order flow shows estimated arrival windows for your address so you can pick a date that is feasible. When in doubt, schedule further ahead for peace of mind.',
        },
        {
          q: 'Can I cancel a mailed card order?',
          a: 'Pending mailed-card pre-orders can be cancelled in Scheduled Smiles before they are placed with our mailing partner. Once the order has been placed with the partner, it cannot be cancelled or changed through the app.',
        },
        {
          q: FaqQuestions.SubscriptionCoversMailedCards,
          a: faqMailedCardPricingAnswer,
        },
        {
          q: 'Can I add a gift card to a mailed card?',
          a: 'When gift cards are available, you can add an optional digital gift card during scheduling. You pay the mailed-card price plus the gift-card face value you select and a small gift card fulfillment fee; all amounts are shown before you confirm.',
        },
      ],
    },
  ],
} as const;
